import { useNavigate } from "@tanstack/react-router";
import type { Decimal } from "decimal.js";
import {
  CheckSquare2Icon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  EyeIcon,
  FileUpIcon,
  FlaskConicalIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  TestTube2Icon,
  TestTubeIcon,
  TrashIcon,
  TriangleIcon,
  XCircleIcon,
  XSquareIcon,
} from "lucide-react";
import React from "react";
import {
  Collection,
  Focusable,
  type Key,
  Tree,
  TreeItem,
  TreeItemContent,
} from "react-aria-components";

import { extendResult, isCancelled } from "@blis/shared/models/result";

import { PermissionGate } from "@/components/PermissionGate";
import { RoleCondition } from "@/components/RoleCondition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { StatusIcon } from "@/components/ui/icons";
import {
  Menu,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";
import type { TTestOrder, TTestOrderResult } from "@/lib/types";
import { convertUnitsToSuperscript } from "@/lib/utils/units";
import { useNavLocationState } from "@/utils/useNavStack";
import { incompleteReasonSummary } from "../../lib/incomplete-reason";
import { useAccessionCommands } from "../AccessionCommands";
import { useAccession } from "../AccessionProvider";
import { TestResultDetailsDialog } from "../dialogs/TestResultDetailsDialog";
import { useSetDialogState } from "./state";

type Sample = {
  id: number;
  displayId: string | null;
  tube: {
    id: number;
    name: string;
  };
};

type BaseTestOrderTreeItem = {
  id: number;
  key: string;
  name: string;
};

type ResultTreeItem = BaseTestOrderTreeItem & {
  type: "result";
  order: TTestOrder;
  result: TTestOrderResult;
  isReflex?: boolean;
  children: TestOrderTreeItem[];
  panelId?: number;
};

type TestTreeItem = BaseTestOrderTreeItem & {
  type: "test";
  order: TTestOrder;
  panelId?: number;
  children: TestOrderTreeItem[];
};

type PanelTreeItem = BaseTestOrderTreeItem & {
  type: "panel";
  order: TTestOrder;
  children: (TestTreeItem | ResultTreeItem)[];
};

type SampleTreeItem = BaseTestOrderTreeItem & {
  type: "sample";
  sample: Sample;
  children: (TestTreeItem | ResultTreeItem)[];
};

type HeaderTreeItem = BaseTestOrderTreeItem & {
  type: "header";
  children: TestOrderTreeItem[];
};

type TestOrderTreeItem =
  | ResultTreeItem
  | TestTreeItem
  | PanelTreeItem
  | SampleTreeItem
  | HeaderTreeItem;

export type TestOrderTreeGroupBy = "none" | "panel" | "sample";

export function TestOrderTree({
  groupBy = "none",
}: {
  groupBy?: TestOrderTreeGroupBy;
}) {
  const { items, allKeys, isLoading } = useTestOrderTree(groupBy);
  const [expandedKeys, setExpandedKeys] = React.useState<Set<Key>>(allKeys);

  // always expand the tree when the keys change
  React.useEffect(() => {
    if (allKeys.size > 0) {
      setExpandedKeys(allKeys);
    }
  }, [allKeys]);

  if (isLoading) {
    return <LoadingItems />;
  }

  if (items.length === 0) {
    return <EmptyTestOrderTree />;
  }

  return (
    <Tree
      items={items}
      aria-label="Test orders"
      className="divide-y border-b"
      expandedKeys={expandedKeys}
      onExpandedChange={setExpandedKeys}
    >
      {function renderItem(item) {
        return (
          <TreeItem
            textValue={item.name}
            hasChildItems={item.children.length > 0}
            className={cn(
              "flex items-center min-h-10 justify-between group/item p-1 hover:bg-accent-muted",
              "outline-hidden focus-visible:rounded-md focus-visible:ring-inset ring-ring/70 focus-visible:ring-2",
              "pl-[calc((var(--tree-item-level)_-_1)_*_1rem+0.25rem)] sm:pl-[calc((var(--tree-item-level)_-_1)_*_2rem+0.75rem)] pr-1 sm:pr-3",
              (item.type === "panel" ||
                item.type === "header" ||
                item.type === "sample") &&
                "bg-accent-muted",
              item.type === "panel" &&
                item.children.every((c) => isCancelled(c.order)) &&
                "line-through",
            )}
          >
            <TreeItemContent>
              {({ isExpanded }) => (
                <>
                  <div className="flex items-center gap-2 min-w-0">
                    {/* chevron - hidden until item is hovered */}
                    {item.children.length > 0 && (
                      <Button
                        slot="chevron"
                        size="icon-sm"
                        variant="ghost"
                        className={cn(
                          "group-data-[expanded=true]/item:rotate-90",
                          item.type !== "header" &&
                            item.type !== "test" &&
                            "hidden group-hover/item:inline-flex",
                        )}
                      >
                        <ChevronRightIcon />
                      </Button>
                    )}
                    {/* result icon - hidden when the item is hovered */}
                    {item.type === "result" && (
                      <div
                        className={cn(
                          "size-6 flex items-center justify-center shrink-0",
                          item.children.length > 0 && "group-hover/item:hidden",
                        )}
                      >
                        <ResultIcon result={item.result} />
                      </div>
                    )}
                    {/* show reflex icon first if it is a reflex test */}
                    {item.type === "result" &&
                      "isReflex" in item &&
                      item.isReflex && (
                        <RefreshCwIcon className="size-3 text-muted-foreground shrink-0" />
                      )}
                    {/* panel icon for panel headers */}
                    {item.type === "panel" && (
                      <div
                        className={cn(
                          "size-6 flex items-center justify-center shrink-0",
                          item.children.length > 0 && "group-hover/item:hidden",
                        )}
                      >
                        <ClipboardListIcon className="size-4 text-muted-foreground shrink-0" />
                      </div>
                    )}
                    {/* sample icon and display ID badge for sample headers */}
                    {item.type === "sample" && (
                      <>
                        <div
                          className={cn(
                            "size-6 flex items-center justify-center shrink-0",
                            item.children.length > 0 &&
                              "group-hover/item:hidden",
                          )}
                        >
                          <TestTube2Icon className="size-4 text-muted-foreground shrink-0" />
                        </div>
                        <Badge variant="outline" size="sm">
                          {item.sample.displayId || item.sample.id}
                        </Badge>
                      </>
                    )}

                    {/* test name or header text */}
                    <Text
                      className={cn(
                        "text-xs sm:text-sm",
                        item.type === "result" &&
                          isCancelled(item.order, item.result) &&
                          "text-muted-foreground line-through",
                        item.type === "sample" && "sm:text-xs",
                      )}
                      truncate
                      bold={
                        item.type === "panel" ||
                        item.type === "header" ||
                        item.type === "sample"
                      }
                    >
                      {item.name}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isExpanded && item.children.length > 0 && (
                      <Badge variant="outline">{item.children.length}</Badge>
                    )}
                    {/* sample assignment menu */}
                    <RoleCondition
                      roles={[
                        "blis_lab_manager",
                        "blis_lab_tech",
                        "blis_lab_assistant",
                      ]}
                    >
                      <SampleAssignment item={item} />
                    </RoleCondition>
                    {/* test result text/badge */}
                    {item.type === "result" && item.result && (
                      <ResultText order={item.order} result={item.result} />
                    )}
                    {/* menu for test/panel/sample headers */}
                    <RoleCondition
                      roles={[
                        "blis_lab_manager",
                        "blis_lab_tech",
                        "blis_lab_assistant",
                      ]}
                    >
                      {item.type !== "header" && item.type !== "sample" && (
                        <TestOrderItemMenu item={item} />
                      )}
                    </RoleCondition>
                  </div>
                </>
              )}
            </TreeItemContent>
            <Collection items={item.children}>{renderItem}</Collection>
          </TreeItem>
        );
      }}
    </Tree>
  );
}

function SampleAssignment({ item }: { item: TestOrderTreeItem }) {
  const setAssignSamples = useSetDialogState("assignSample");

  if (item.type !== "result" || item.order.samples.length > 0) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onPress={() => {
        setAssignSamples({
          orderId: item.order.id,
          currentSampleIds: item.order.samples.map((s) => s.sampleId),
        });
      }}
    >
      <TestTube2Icon />
      Assign
    </Button>
  );
}

function useTestOrderTree(groupBy: TestOrderTreeGroupBy = "none") {
  const { testOrders, samples: allSamples, testOrdersLoading } = useAccession();

  return React.useMemo(() => {
    // Recursive function to process dependencies
    const processDependencies = (order: TTestOrder): TestOrderTreeItem[] => {
      const dependencyItems: TestOrderTreeItem[] = [];

      for (const dep of order.dependencies) {
        let depItem: TestTreeItem | ResultTreeItem = createTestTreeItem(dep);

        // Handle results for this dependency
        if (dep.results.length === 1 && !dep.testSpec.alwaysShowHeader) {
          depItem = {
            ...createResultTreeItem(dep, dep.results[0]),
            children: depItem.children,
          };
        } else if (dep.results.length > 1 || dep.testSpec.alwaysShowHeader) {
          for (const result of dep.results) {
            depItem.children.push(createResultTreeItem(dep, result));
          }
        }

        // Recursively process nested dependencies
        const nestedDeps = processDependencies(dep);
        depItem.children.push(...nestedDeps);

        dependencyItems.push(depItem);
      }

      return dependencyItems;
    };

    const items: TestOrderTreeItem[] = [];
    const panels: TestOrderTreeItem[] = [];
    const tests: TestOrderTreeItem[] = [];
    const samples: TestOrderTreeItem[] = [];

    for (const order of testOrders) {
      let testItem: TestTreeItem | ResultTreeItem = createTestTreeItem(order);

      // if there is only one result and the test does not always show header, show the result only
      if (order.results.length === 1 && !order.testSpec.alwaysShowHeader) {
        testItem = {
          ...createResultTreeItem(order, order.results[0]),
          children: testItem.children,
        };

        // otherwise show the test as a header with children
      } else if (order.results.length > 1 || order.testSpec.alwaysShowHeader) {
        for (const result of order.results) {
          testItem.children.push(createResultTreeItem(order, result));
        }
      }

      // handle dependencies and reflex tests recursively
      const dependencyItems = processDependencies(order);
      testItem.children.push(...dependencyItems);

      if (groupBy === "sample") {
        const actualSampleId =
          order.results.length > 0 && order.results[0].sample?.id
            ? order.results[0].sample.id
            : order.samples[0]?.sampleId;

        if (actualSampleId) {
          // Find the sample to use (either from results or assigned samples)
          const sampleToUse =
            order.results.length > 0 && order.results[0].sample
              ? order.results[0].sample // Use sample from result
              : order.samples[0]?.sample; // Use assigned sample

          const sample = samples.find((s) => s.id === actualSampleId);
          if (!sample && sampleToUse) {
            const sampleItem = createSampleTreeItem(sampleToUse);
            sampleItem.children.push(testItem);
            samples.push(sampleItem);
          } else if (sample) {
            sample.children.push(testItem);
          } else {
            // Fallback if sample data is missing
            tests.push(testItem);
          }
        } else {
          tests.push(testItem);
        }
      } else if (groupBy === "panel") {
        // handle panels with child tests or child panels
        if (order.instruction?.panel) {
          const panelItem = createPanelTreeItem(order, order.instruction.panel);
          const panel = panels.find(
            (p) => p.id === order.instruction?.panel?.id,
          );
          if (!panel) {
            panelItem.children.push(testItem);
            panels.push(panelItem);
          } else {
            panel.children.push(testItem);
          }
        } else {
          tests.push(testItem);
        }
      } else {
        items.push(testItem);
      }
    }

    if (groupBy === "sample") {
      const headerItem = createHeaderTreeItem("No sample assigned");
      if (samples.length > 0) {
        items.push(...samples);
        if (tests.length > 0) {
          headerItem.children.push(...tests);
          items.push(headerItem);
        }
      } else {
        headerItem.children.push(...tests);
        items.push(headerItem);
      }

      for (const sample of allSamples) {
        const sampleItem = createSampleTreeItem(sample);
        if (!items.find((s) => s.key === sampleItem.key)) {
          sampleItem.name = `${sample.tube.name} - no assigned tests`;
          items.push(sampleItem);
        }
      }
    } else if (groupBy === "panel") {
      const hasPanels = panels.length > 0;
      const headerItem = createHeaderTreeItem("No panel - A la carte tests");
      if (hasPanels) {
        items.push(...panels);
        if (tests.length > 0) {
          headerItem.children.push(...tests);
          items.push(headerItem);
        }
      } else {
        headerItem.children.push(...tests);
        items.push(headerItem);
      }
    } else {
      items.push(...tests);
    }

    return {
      items: items,
      allKeys: getAllKeys(items),
      isLoading: testOrdersLoading,
    };
  }, [testOrders, testOrdersLoading, allSamples, groupBy]);
}

function LoadingItems() {
  return (
    <div className="flex flex-col divide-y border-b">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between px-3 h-10">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyTestOrderTree() {
  const setShowAddTests = useSetDialogState("addTests");

  return (
    <EmptyState>
      <EmptyStateIcon>
        <FlaskConicalIcon />
      </EmptyStateIcon>
      <EmptyStateHeading>No test orders</EmptyStateHeading>
      <EmptyStateDescription>
        No test orders have been added to this accession.
      </EmptyStateDescription>
      <RoleCondition
        roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
      >
        <EmptyStateActions>
          <Button size="sm" onPress={() => setShowAddTests(true)}>
            <PlusIcon />
            Add tests
          </Button>
        </EmptyStateActions>
      </RoleCondition>
    </EmptyState>
  );
}

function ResultIcon({
  result,
  className,
}: {
  result: TTestOrderResult;
  className?: string;
}) {
  switch (result.status) {
    case "final":
      return <StatusIcon status="final" className={className} />;
    case "validating":
      return (
        <StatusIcon status="in_progress" color="blue" className={className} />
      );
    case "reflex pending":
    case "in progress":
      return <StatusIcon status="in_progress" className={className} />;
    case "pending":
      return <StatusIcon status="new" color="gray" className={className} />;
    case "cancelled":
      return (
        <StatusIcon status="cancelled" color="gray" className={className} />
      );
    default: {
      const _exhaustive: never = result.status;
      void _exhaustive;
      return <StatusIcon status="new" color="red" className={className} />;
    }
  }
}

function ResultText({
  order,
  result,
}: {
  order: TTestOrder;
  result: TTestOrderResult | undefined;
}) {
  if (!result) {
    return null;
  }

  const extended = extendResult(result);
  const isFreeText = result.testResultSpec.type === "FREE_TEXT";
  const incompleteReason =
    result.status === "reflex pending" && "incompleteReason" in result
      ? result.incompleteReason
      : undefined;

  const rangeConflicts =
    result.conflicts?.filter((c) => c.range !== null) ?? [];
  const deltaConflicts =
    result.conflicts?.filter((c) => c.delta !== null) ?? [];

  if (isCancelled(order, extended)) {
    return (
      <Text size="2" muted>
        Cancelled
      </Text>
    );
  }

  if (extended.$final) {
    if (extended.finalResult?.color) {
      return (
        <Badge
          color={extended.finalResult.color as "red"}
          className="whitespace-nowrap"
        >
          {extended.$final.toString(true)}
        </Badge>
      );
    }
    return (
      <>
        {rangeConflicts.map((c) => (
          <Badge key={c.id} color="yellow" size="sm">
            {/* {c.range?.type === "H" ? (
                <ArrowUpIcon data-slot="icon" />
              ) : (
                <ArrowDownIcon data-slot="icon" />
              )} */}
            {c.range?.type === "H" ? "High" : "Low"}
          </Badge>
        ))}
        {deltaConflicts.map((c) => (
          <DeltaCheckBadge
            key={c.id}
            conflict={c}
            units={result.testResultSpec.units}
          />
        ))}
        <Text
          bold
          elementType={isFreeText ? "pre" : "span"}
          className={cn(
            "text-xs sm:text-sm text-nowrap",
            isFreeText && "text-[10px] lg:text-xs py-2 pr-2 text-wrap",
          )}
        >
          <RoleCondition
            roles={["blis_lab_manager", "blis_lab_tech", "blis_lab_assistant"]}
            fallback={convertUnitsToSuperscript(
              extended.$final.getRangeText(true),
            )}
          >
            {convertUnitsToSuperscript(extended.$final.toString(true))}
          </RoleCondition>
        </Text>
      </>
    );
  }

  if (extended) {
    // Status lives on the right-side label — not the left icon — because parent
    // rows swap the icon for the expand chevron on hover.
    const statusLabel = (
      <Text
        size="2"
        muted
        className={cn(
          "first-letter:capitalize",
          incompleteReason && "underline decoration-dotted underline-offset-2",
        )}
      >
        {extended.status}
      </Text>
    );

    if (!incompleteReason) {
      return statusLabel;
    }

    return (
      <TooltipTrigger delay={100}>
        <Focusable>{statusLabel}</Focusable>
        <Tooltip>{incompleteReasonSummary(incompleteReason)}</Tooltip>
      </TooltipTrigger>
    );
  }

  return null;
}

function TestOrderItemMenu({
  item,
}: {
  item: PanelTreeItem | TestTreeItem | ResultTreeItem;
}) {
  const order = item.order;

  const setEnterResult = useSetDialogState("enterResults");
  const setUpdateResult = useSetDialogState("updateTestResult");
  const setClearResult = useSetDialogState("clearTestResult");
  const setCancelTestOrder = useSetDialogState("cancelTestOrder");
  const setDeleteTestOrder = useSetDialogState("deleteTestOrder");
  const setRestoreTestOrder = useSetDialogState("restoreTestOrder");
  const setCancelTestResult = useSetDialogState("cancelTestResult");
  const setRestoreTestResult = useSetDialogState("restoreTestResult");
  const setCancelPanel = useSetDialogState("cancelPanel");
  const setRestorePanel = useSetDialogState("restorePanel");
  const setDeletePanel = useSetDialogState("deletePanel");
  const setAssignSample = useSetDialogState("assignSample");
  const { createTestRun } = useAccessionCommands();

  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const result = item.type === "result" ? extendResult(item.result) : undefined;
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();

  const onAction = (action: Key) => {
    switch (action) {
      case "viewDetails":
        setDetailsOpen(true);
        break;
      case "createTestRun":
        createTestRun([order.testId]);
        break;
      case "viewTestRun": {
        const toHref = order.testSpec.processTemplateVersionId
          ? "/runs/$id/workspace"
          : "/runs/$id";
        navigate({
          to: toHref,
          params: {
            id: order.testRunId ?? -1,
          },
          search: {
            tab: "overview",
            day: null,
          },
          state: { href, label },
        });
        break;
      }
      case "viewWorklist":
        navigate({
          to: `/runs/${order.testRunId}/worklist/${order.testSpec.id}`,
          params: {
            id: order.testRunId ?? -1,
            testId: order.testSpec.id,
          },
        });
        break;
      case "enterResult":
        setEnterResult({
          orderId: order.dependedById || order.id,
          resultId: result?.id,
        });
        break;
      case "updateResult":
        setUpdateResult(result?.id ? { resultId: result.id } : null);
        break;
      case "clearResult":
        setClearResult(result?.id ? { resultId: result.id } : null);
        break;
      case "cancelOrder":
        setCancelTestOrder({ order });
        break;
      case "restoreOrder":
        setRestoreTestOrder({ orderId: order.id });
        break;
      case "cancelResult":
        setCancelTestResult(result?.id ? { resultId: result.id } : null);
        break;
      case "restoreResult":
        setRestoreTestResult(result?.id ? { resultId: result.id } : null);
        break;
      case "deleteOrder":
        setDeleteTestOrder({ orderId: order.id });
        break;
      case "cancelPanel":
        if (order.instruction) {
          setCancelPanel({ instructionId: order.instruction.id });
        }
        break;
      case "restorePanel":
        if (order.instruction) {
          setRestorePanel({ instructionId: order.instruction.id });
        }
        break;
      case "deletePanel":
        if (order.instruction) {
          setDeletePanel({ instructionId: order.instruction.id });
        }
        break;
      case "assignSample":
        setAssignSample({
          orderId: order.id,
          currentSampleIds: order.samples.map((s) => s.sampleId),
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <MenuTrigger>
        <Button size="icon-sm" variant="ghost">
          <MoreHorizontalIcon />
        </Button>
        <Menu aria-label="Test order actions" onAction={onAction}>
          {item.type === "panel" && <PanelItemMenu item={item} />}
          {item.type === "test" && <TestItemMenu item={item} />}
          {item.type === "result" && <ResultItemMenu item={item} />}
        </Menu>
      </MenuTrigger>
      {result && (
        <TestResultDetailsDialog
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          result={result}
          order={order}
        />
      )}
    </>
  );
}

function PanelItemMenu({ item }: { item: PanelTreeItem }) {
  const hasCancelledOrders = item.children.some(
    (c) =>
      (c.type === "result" && isCancelled(c.order, c.result)) ||
      (c.type === "test" && isCancelled(c.order)),
  );
  const isPanelCancelled = item.children.every(
    (c) =>
      (c.type === "result" && isCancelled(c.order, c.result)) ||
      (c.type === "test" && isCancelled(c.order)),
  );

  return (
    <>
      {hasCancelledOrders && (
        <>
          <MenuItem id="restorePanel">
            <FileUpIcon data-slot="icon" />
            Restore panel
          </MenuItem>
          <MenuSeparator />
        </>
      )}
      <MenuItem id="cancelPanel" isDisabled={isPanelCancelled}>
        <XCircleIcon data-slot="icon" />
        Cancel panel
      </MenuItem>
      <MenuItem id="deletePanel" isDanger isDisabled={isInProgress(item)}>
        <TrashIcon data-slot="icon" />
        Delete panel
      </MenuItem>
    </>
  );
}

function TestItemMenu({ item }: { item: TestTreeItem }) {
  const { accession } = useAccession();
  const order = item.order;

  return (
    <>
      {!order.testRunId && accession.status === "received" && (
        <MenuItem id="createTestRun">
          <FlaskConicalIcon data-slot="icon" />
          Create test run
        </MenuItem>
      )}
      {order.testRunId && (
        <>
          <MenuItem id="viewTestRun" isDisabled={!order.testRunId}>
            <FlaskConicalIcon data-slot="icon" />
            View test run
          </MenuItem>
          <MenuItem id="viewWorklist" isDisabled={!order.testRunId}>
            <ClipboardCheckIcon data-slot="icon" />
            View worklist
          </MenuItem>
        </>
      )}
      <MenuSeparator />
      <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
        <MenuItem id="enterResult" isDisabled={!accession.received}>
          <CheckSquare2Icon data-slot="icon" />
          Enter result
        </MenuItem>
      </RoleCondition>
      <MenuItem id="assignSample">
        <TestTubeIcon data-slot="icon" />
        Assign sample
      </MenuItem>
      <MenuSeparator />

      {order.cancelled ? (
        <MenuItem id="restoreOrder" isDisabled={accession.cancelled}>
          <FileUpIcon data-slot="icon" />
          Restore test
        </MenuItem>
      ) : (
        <MenuItem id="cancelOrder">
          <XCircleIcon data-slot="icon" />
          Cancel test
        </MenuItem>
      )}
      {/* for now only provide the option to delete if not part of a panel */}
      {!order.instruction?.panelId && !order.dependedById && (
        <MenuItem id="deleteOrder" isDanger isDisabled={isInProgress(item)}>
          <TrashIcon data-slot="icon" />
          Delete test
        </MenuItem>
      )}
    </>
  );
}

function ResultItemMenu({ item }: { item: ResultTreeItem }) {
  const { accession } = useAccession();
  const order = item.order;
  const result = item.result ? extendResult(item.result) : undefined;
  const isCombined = order.results.length === 1;

  return (
    <>
      <MenuItem id="viewDetails">
        <EyeIcon data-slot="icon" />
        View details
      </MenuItem>
      {!order.testRunId &&
        ["received", "in_progress"].includes(accession.status) && (
          <MenuItem id="createTestRun">
            <FlaskConicalIcon data-slot="icon" />
            Create test run
          </MenuItem>
        )}
      {order.testRunId && (
        <>
          <MenuItem id="viewTestRun" isDisabled={!order.testRunId}>
            <FlaskConicalIcon data-slot="icon" />
            View test run
          </MenuItem>
          <MenuItem id="viewWorklist" isDisabled={!order.testRunId}>
            <ClipboardCheckIcon data-slot="icon" />
            View worklist
          </MenuItem>
        </>
      )}
      <MenuSeparator />
      <MenuItem id="assignSample">
        <TestTubeIcon data-slot="icon" />
        Assign sample
      </MenuItem>
      <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
        <MenuItem id="enterResult" isDisabled={!accession.received}>
          <CheckSquare2Icon data-slot="icon" />
          Enter result
        </MenuItem>
      </RoleCondition>
      <PermissionGate requiredPermission="update:results">
        <MenuItem id="updateResult">
          <PencilIcon data-slot="icon" />
          Update result
        </MenuItem>
      </PermissionGate>
      <MenuSeparator />
      <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
        <MenuItem
          id="clearResult"
          isDisabled={
            order && (isCancelled(order, result) || !result?.$initial)
          }
        >
          <XSquareIcon data-slot="icon" />
          Clear result
        </MenuItem>
      </RoleCondition>
      {isCombined ? (
        <>
          {order.cancelled ? (
            <MenuItem id="restoreOrder" isDisabled={accession.cancelled}>
              <FileUpIcon data-slot="icon" />
              Restore test
            </MenuItem>
          ) : (
            <MenuItem id="cancelOrder">
              <XCircleIcon data-slot="icon" />
              {order.isReflex ? "Cancel reflex" : "Cancel test"}
            </MenuItem>
          )}
          {/* for now only provide the option to delete if not part of a panel */}
          {!order.instruction?.panelId && (
            <MenuItem id="deleteOrder" isDanger>
              <TrashIcon data-slot="icon" />
              Delete test
            </MenuItem>
          )}
        </>
      ) : isCancelled(order, result) ? (
        <MenuItem id="restoreResult" isDisabled={accession.cancelled}>
          <FileUpIcon data-slot="icon" />
          Restore result
        </MenuItem>
      ) : (
        <MenuItem id="cancelResult">
          <XCircleIcon data-slot="icon" />
          Cancel result
        </MenuItem>
      )}
    </>
  );
}

type DeltaComparedToResult = {
  accessionId: number | null;
  resultNumeric: Decimal | null;
  finalResultNumeric: Decimal | null;
  result: { value: string; description: string } | null;
  finalResult: { value: string; description: string } | null;
} | null;

type DeltaCheckConflict = {
  id: number;
  delta: {
    delta: Decimal | null;
    type: "PERCENT" | "ABSOLUTE" | "QUALITATIVE";
    comparedToResult: DeltaComparedToResult;
  } | null;
};

function DeltaCheckBadge({
  conflict,
  units,
}: {
  conflict: DeltaCheckConflict;
  units: string | null;
}) {
  const delta = conflict.delta;
  if (!delta) {
    return null;
  }

  if (delta.type === "QUALITATIVE") {
    return <QualitativeDeltaBadge comparedToResult={delta.comparedToResult} />;
  }

  if (delta.delta === null) {
    return null;
  }

  const numericDelta = delta.delta;
  const verb = numericDelta.gt(0) ? "increased" : "decreased";
  // Show the value the delta was actually computed against — the prior
  // accession's finalized value (falling back to the entered value).
  const previousNumeric =
    delta.comparedToResult?.finalResultNumeric ??
    delta.comparedToResult?.resultNumeric;

  return (
    <TooltipTrigger delay={100}>
      <Focusable>
        <Badge color="red" size="sm">
          <TriangleIcon className="size-3" />
          {numericDelta.gt(0) && "+"}
          {numericDelta.toFixed(1)}
          {delta.type === "PERCENT" ? "%" : ""}
        </Badge>
      </Focusable>
      <Tooltip className="p-0 shadow-md">
        <div className="p-2 border-b flex items-center gap-2">
          <TriangleIcon className="size-3 text-destructive" />
          <Text bold>Delta check</Text>
        </div>
        <div className="p-2 flex flex-col gap-1">
          <Text>
            Result {verb} by:{" "}
            <strong className="font-medium">
              {numericDelta.toFixed(1)}{" "}
              {delta.type === "PERCENT" ? "%" : units ? ` ${units}` : ""}
            </strong>
          </Text>
          {previousNumeric && (
            <Text>
              Previous result:{" "}
              <strong className="font-medium">
                {previousNumeric.toFixed(1)}
                {units ? ` ${units}` : ""}
              </strong>
            </Text>
          )}
          {delta.comparedToResult?.accessionId && (
            <Text>
              See accession:{" "}
              <a
                href={`/accessions/${delta.comparedToResult.accessionId}`}
                target="_blank"
                rel="noreferrer"
                className="font-medium underline"
              >
                {delta.comparedToResult.accessionId}
              </a>
            </Text>
          )}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
}

function QualitativeDeltaBadge({
  comparedToResult,
}: {
  comparedToResult: DeltaComparedToResult;
}) {
  const previousDescription =
    comparedToResult?.finalResult?.description ??
    comparedToResult?.result?.description;

  return (
    <TooltipTrigger delay={100}>
      <Focusable>
        <Badge color="red" size="sm">
          <TriangleIcon className="size-3" />
          Discrepant
        </Badge>
      </Focusable>
      <Tooltip className="p-0 shadow-md">
        <div className="p-2 border-b flex items-center gap-2">
          <TriangleIcon className="size-3 text-destructive" />
          <Text bold>Delta check</Text>
        </div>
        <div className="p-2 flex flex-col gap-1">
          <Text>Result is discrepant with the previous result</Text>
          {previousDescription && (
            <Text>
              Previous result:{" "}
              <strong className="font-medium">{previousDescription}</strong>
            </Text>
          )}
          {comparedToResult?.accessionId && (
            <Text>
              See accession:{" "}
              <a
                href={`/accessions/${comparedToResult.accessionId}`}
                target="_blank"
                rel="noreferrer"
                className="font-medium underline"
              >
                {comparedToResult.accessionId}
              </a>
            </Text>
          )}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * Recursive function to get all keys from a tree of TestOrderTreeItems
 */
function getAllKeys(items: TestOrderTreeItem[]) {
  const keys = new Set<string>();
  for (const item of items) {
    keys.add(item.key);
    if (item.children.length > 0) {
      const childKeys = getAllKeys(item.children);
      for (const key of childKeys) {
        keys.add(key);
      }
    }
  }
  return keys;
}

function _getReflexedFrom({
  result,
  order,
  allOrders,
}: {
  result?: TTestOrderResult;
  order?: TTestOrder;
  allOrders: TTestOrder[];
}) {
  const parentResultId = order?.parentResultId || result?.parentResultId;

  if (!parentResultId) {
    return undefined;
  }

  const results = allOrders.flatMap((o) => o.results);

  return results.find((r) => r.id === parentResultId);
}

function createPanelTreeItem(
  order: TTestOrder,
  panel: { id: number; name: string },
): PanelTreeItem {
  return {
    id: panel.id,
    key: `panel-${panel.id}-${order.id}`,
    name: panel.name,
    type: "panel",
    order,
    children: [],
  };
}

function createSampleTreeItem(sample: Sample): SampleTreeItem {
  return {
    id: sample.id,
    key: `sample-${sample.id}`,
    name: sample.tube.name,
    type: "sample",
    sample,
    children: [],
  };
}

function createTestTreeItem(order: TTestOrder): TestTreeItem {
  return {
    id: order.testSpec.id,
    key: `test-${order.testSpec.id}-${order.id}`,
    name: order.testSpec.name,
    type: "test",
    order,
    panelId: order.instruction?.panel?.id,
    children: [],
  };
}

function createResultTreeItem(
  order: TTestOrder,
  result: TTestOrderResult,
): ResultTreeItem {
  return {
    id: result.id,
    key: `result-${order.testSpec.id}-${result.id}`,
    name: result.testResultSpec.name ?? order.testSpec.name,
    type: "result",
    isReflex: order.isReflex || result.isReflex,
    order,
    result,
    panelId: order.instruction?.panel?.id,
    children: [],
  };
}

function createHeaderTreeItem(name: string): HeaderTreeItem {
  return {
    id: 0,
    key: "header",
    name,
    type: "header",
    children: [],
  };
}

function isInProgress(item: TestOrderTreeItem) {
  const notInProgress = ["cancelled", "pending"];

  if (item.type === "panel" || item.type === "test") {
    return item.children.some((c) => isInProgress(c));
  }

  if (item.type === "result") {
    return !notInProgress.includes(item.result?.status);
  }

  return false;
}
